import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { DataService } from '../data/data.service';

/**
 * Centralised REST API layer for template endpoints on source-service
 * (environment.apiUrlSourceService).
 *
 * Covers:
 *  - Template setup flows  → /template-setups/...
 *  - Flow steps            → /template-setups/flows/steps/...
 *  - Save / Edit           → /template-setups/connector-studio/...
 */
@Injectable({
  providedIn: 'root'
})
export class TemplateSourceService {
  private baseUrl = environment.apiUrlSourceService;

  constructor(private dataService: DataService, private http: HttpClient) {}

  // ──────────────────────────────────────────────
  //  TEMPLATE SETUP FLOWS (CREATE / EDIT)
  // ──────────────────────────────────────────────

  /**
   * Initialise a CREATE template setup flow.
   * GET /template-setups/flows/init
   */
  initTemplateSetupFlow(userId: number, dataSourceType: string, productId: number = 1): Observable<unknown> {
    return this.dataService.get({
      url: `${this.baseUrl}/template-setups/flows/init?userId=${userId}&dataSourceType=${dataSourceType}&productId=${productId}`
    });
  }

  /**
   * Initialise an EDIT template setup flow.
   * GET /template-setups/{templateId}/flows/init
   */
  initTemplateEditFlow(templateId: number | string, userId: number, dataSourceType: string, productId: number = 1): Observable<unknown> {
    return this.dataService.get({
      url: `${this.baseUrl}/template-setups/${templateId}/flows/init?userId=${userId}&dataSourceType=${dataSourceType}&productId=${productId}`
    });
  }

  // ──────────────────────────────────────────────
  //  FLOW STEPS (NEXT / PREVIOUS)
  // ──────────────────────────────────────────────

  /**
   * Get next step in the template setup flow.
   * POST /template-setups/flows/steps/next
   */
  getTemplateNextStep(sessionState: string, currentStepUserInput: Record<string, unknown>, productId: number = 1): Observable<unknown> {
    return this.dataService.post({
      url: `${this.baseUrl}/template-setups/flows/steps/next?productId=${productId}`,
      data: { sessionState, currentStepUserInput }
    });
  }

  /**
   * Get previous step in the template setup flow.
   * POST /template-setups/flows/steps/previous
   */
  getTemplatePreviousStep(sessionState: string, productId: number = 1): Observable<unknown> {
    return this.dataService.post({
      url: `${this.baseUrl}/template-setups/flows/steps/previous?productId=${productId}`,
      data: { sessionState }
    });
  }

  // ──────────────────────────────────────────────
  //  SAVE CONNECTOR STUDIO TEMPLATE
  // ──────────────────────────────────────────────

  /**
   * Save a new connector-studio template (CREATE).
   * POST /template-setups/connector-studio
   */
  saveConnectorStudioTemplate(data: Record<string, unknown>, userId: number, dataSourceId: number, dataSourceType: string, productId: number = 1): Observable<unknown> {
    return this.dataService.post({
      url: `${this.baseUrl}/template-setups/connector-studio?userId=${userId}&dataSourceId=${dataSourceId}&dataSourceType=${dataSourceType}&productId=${productId}`,
      data
    });
  }

  /**
   * Save an edited connector-studio template (EDIT).
   * PUT /template-setups/{templateId}/connector-studio
   */
  saveEditedConnectorStudioTemplate(data: Record<string, unknown>, templateId: number | string, userId: number, productId: number = 1): Observable<unknown> {
    return this.dataService.put({
      url: `${this.baseUrl}/template-setups/${templateId}/connector-studio?userId=${userId}&productId=${productId}`,
      data
    });
  }

  /**
   * Save an edited connector-studio template as draft (EDIT LATER).
   * PUT /template-setups/{templateId}/connector-studio/draft
   */
  saveEditedConnectorStudioTemplateLater(data: Record<string, unknown>, templateId: number | string, userId: number): Observable<unknown> {
    return this.dataService.put({
      url: `${this.baseUrl}/template-setups/${templateId}/connector-studio/draft?userId=${userId}`,
      data
    });
  }

  // ──────────────────────────────────────────────
  //  TEMPLATE MANAGEMENT
  // ──────────────────────────────────────────────

  /**
   * Delete a template.
   * DELETE /template-setups/{templateId}
   */
  deleteTemplate(templateId: number | string, userId: number): Observable<unknown> {
    return this.dataService.delete({
      url: `${this.baseUrl}/template-setups/${templateId}?userId=${userId}`
    });
  }

  /**
   * Get a template by ID.
   * GET /template-setups/{templateId}
   */
  getTemplate(templateId: number | string, userId: number): Observable<unknown> {
    return this.dataService.get({
      url: `${this.baseUrl}/template-setups/${templateId}?userId=${userId}`
    });
  }

  /**
   * Get all templates.
   * GET /template-setups
   */
  getAllTemplates(userId: number): Observable<unknown> {
    return this.dataService.get({
      url: `${this.baseUrl}/template-setups?userId=${userId}`
    });
  }

  /**
   * Get templates by type.
   * GET /template-setups?type={type}
   */
  getTemplatesByType(userId: number, type: string): Observable<unknown> {
    return this.dataService.get({
      url: `${this.baseUrl}/template-setups?type=${type}&userId=${userId}`
    });
  }

  /**
   * Get template names.
   * GET /template-setups/names
   */
  getTemplateNames(userId: number): Observable<unknown> {
    return this.dataService.get({
      url: `${this.baseUrl}/template-setups/names?userId=${userId}`
    });
  }

  /**
   * Get active integrations for a template.
   * GET /template-setups/{templateId}/integrations
   */
  getActiveTemplateIntegrations(templateId: number | string, userId: number): Observable<unknown> {
    return this.dataService.get({
      url: `${this.baseUrl}/template-setups/${templateId}/integrations?userId=${userId}`
    });
  }

  // ──────────────────────────────────────────────
  //  TEMPLATE SOURCE OPERATIONS
  // ──────────────────────────────────────────────

  /**
   * Apply latest template changes to source IDs (bulk).
   * POST /template-setups/{templateId}/apply?userId=
   */
  bulkApplyTemplateChanges(templateId: number, sourceIDs: number[], userId: number): Observable<unknown> {
    return this.dataService.post({
      url: `${this.baseUrl}/template-setups/${templateId}/apply?userId=${userId}`,
      data: sourceIDs
    });
  }

  /**
   * Get active template list.
   * GET /template-setups/active?userId={userId}
   */
  getActiveTemplateList(userId: number): Observable<unknown> {
    return this.dataService.get({
      url: `${this.baseUrl}/template-setups/active?userId=${userId}`
    });
  }

  /**
   * De-link a single source from a template.
   * DELETE /template-setups/{templateId}/sources/{sourceId}
   */
  delinkSourceFromTemplate(templateId: number, sourceId: number): Observable<unknown> {
    return this.dataService.delete({
      url: `${this.baseUrl}/template-setups/${templateId}/sources/${sourceId}`
    });
  }

  /**
   * Bulk de-link integrations from a template.
   * DELETE /template-setups/{templateId}/sources?userId={userId}
   * Uses HttpClient directly because the body is required and DataService.delete does not support it.
   * Auth headers are added globally by the HTTP interceptor.
   */
  bulkDelinkIntegrationsFromTemplate(templateId: number, userId: number, sourceIDs: number[]): Observable<unknown> {
    return this.http.delete<unknown>(
      `${this.baseUrl}/template-setups/${templateId}/sources?userId=${userId}`,
      { body: sourceIDs }
    );
  }

}
